/**
 * Batch 18-F15.1 — Provenance labels for training evidence display.
 *
 * Implements the Option B "badge + provenance line" recommendation
 * recorded in docs/training/TRAINING_EVIDENCE_VISIBILITY_DECISION_2026-06-04.md.
 *
 * Pure deterministic helpers — same input always returns the same
 * label, no I/O, no data fetch, no React import. Each function answers
 * exactly one question: what is the human-readable provenance line for
 * THIS evidence trust class? Trust classes never share labels.
 *
 * Coverage:
 *
 *   - Manager verification → 'Human-attested verification'
 *   - Manual / user-declared completion → 'Self-attested completion'
 *   - Manager revocation, by flavor:
 *       manual revoke            → 'Human reversal'
 *       auto-revoke trigger      → 'System safety reversal'
 *       one-shot reconciliation  → 'System reconciliation'
 *
 * Explicitly NOT covered (load-bearing — see F15 §1 / §3 / §10 / §11):
 *
 *   - Extension evidence (Companion observation). Display is deferred
 *     to a later batch. By construction these helpers cannot emit a
 *     provenance string for extension input, and a future caller
 *     wiring extension provenance MUST add a new function — not reuse
 *     these — so extension can never be mislabelled as 'verified' or
 *     'manager verified'.
 *   - Provider / LMS callback evidence. The provider table does not
 *     exist yet (F14 audit-only). These helpers refuse to label
 *     anything as provider-attested.
 *
 * Privacy: returns only the fixed label constants below. Never returns
 * raw URLs, query strings, page titles, headers, tokens, signatures,
 * provider payloads, internal IDs, or arbitrary metadata. Callers can
 * safely render the return value as-is anywhere a label is allowed.
 */

// ─── Label constants ────────────────────────────────────────────────────

export const VERIFICATION_PROVENANCE_LABEL =
  'Human-attested verification' as const;

export const MANUAL_COMPLETION_PROVENANCE_LABEL =
  'Self-attested completion' as const;

export const MANAGER_REVERSAL_PROVENANCE_LABEL = 'Human reversal' as const;

export const SYSTEM_SAFETY_REVERSAL_PROVENANCE_LABEL =
  'System safety reversal' as const;

export const SYSTEM_RECONCILIATION_PROVENANCE_LABEL =
  'System reconciliation' as const;

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Provenance line for a manager-verified training session.
 *
 * Returns the constant 'Human-attested verification'. Manager
 * verification is the only path allowed to set
 * completion_confidence='verified' today (F15 §3.D), and this is the
 * label that explains that on the user-facing badge.
 */
export function getVerificationProvenanceLabel(): string {
  return VERIFICATION_PROVENANCE_LABEL;
}

/**
 * Provenance line for a self-declared completion. The `kind` argument
 * exists so future completion sources (e.g. provider auto-complete)
 * cannot reuse this function silently — only the explicit 'manual'
 * branch returns a label.
 */
export function getCompletionProvenanceLabel(kind: 'manual'): string {
  if (kind === 'manual') return MANUAL_COMPLETION_PROVENANCE_LABEL;
  return '';
}

export interface RevocationProvenanceInput {
  /** True when the auto-revoke trigger fired on completion_status change. */
  autoRevoked?: boolean | null;
  /**
   * True when the one-shot reconciliation DO block repaired a
   * historically stale verified state. Overrides `autoRevoked` because
   * it is a more specific signal (matches the existing reason-line
   * precedence in TrainingRevocationBadge).
   */
  oneShotReconciliation?: boolean | null;
}

/**
 * Provenance line for a manager-revoked training session. Three
 * flavors map to three constants per the F15.1 spec:
 *
 *   - oneShotReconciliation  → 'System reconciliation'   (wins)
 *   - autoRevoked            → 'System safety reversal'
 *   - otherwise              → 'Human reversal'
 *
 * The precedence mirrors the existing reason-line logic in
 * TrainingRevocationBadge, so the provenance row and the flavor row
 * always agree.
 */
export function getRevocationProvenanceLabel(
  input: RevocationProvenanceInput,
): string {
  if (input.oneShotReconciliation) return SYSTEM_RECONCILIATION_PROVENANCE_LABEL;
  if (input.autoRevoked) return SYSTEM_SAFETY_REVERSAL_PROVENANCE_LABEL;
  return MANAGER_REVERSAL_PROVENANCE_LABEL;
}
